// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  usePropsValidation,
  types as t,
} from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import Latex from './Latex.js';
import Figure from './Figure.js';
import { LatexTextAtom } from './LatexTextAtom.js';
import { illustrationColor } from './designSystem.js';

// Order of evaluation is important here, this must be defined before
// usage so that the nonEmptyLatexChecker.description assignment has occurred.
function nonEmptyLatexChecker(props, propName) {
  const value = props[propName];

  const failure = t.latex(props, propName);
  if (failure !== null) return failure;

  // Refinement for Flow as it doesn't know t.latex enforces a string type
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  if (value.trim().length === 0) {
    return {
      typeDescription: nonEmptyLatexChecker.description,
      reason: 'must not be an empty latex expression.',
    };
  }

  return null;
}
nonEmptyLatexChecker.description = 'NonEmptyLatexExpression';

export function AlgebraicWorking(props: {|
  useRowNumbering?: boolean,
  children: ChildrenArray<Element<typeof AlgebraicWorkingRow>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, AlgebraicWorking);
  if (failedValidation) return null;
  return (
    <Figure>
      <table
        style={{
          margin: '0 auto',
          padding: '0 10px',
          borderSpacing: 2,
          borderCollapse: 'separate',
        }}
      >
        <tbody>
          {React.Children.toArray(props.children).map((c, i) =>
            React.cloneElement(c, {
              _index: i,
              _useRowNumbering: Boolean(props.useRowNumbering),
            }),
          )}
        </tbody>
      </table>
    </Figure>
  );
}
AlgebraicWorking.spec = {
  useRowNumbering: t.optional(t.boolean),
  children: t.childrenArray(t.elementType(AlgebraicWorkingRow)),
};

const TD_STYLE = {
  verticalAlign: 'top',
  textAlign: 'left',
  padding: 1,
  paddingBottom: 15,
  whiteSpace: 'nowrap',
};

export function AlgebraicWorkingRow(props: {|
  _index: number,
  _useRowNumbering: boolean,
  children:
    | Element<typeof AlgebraicWorkingStatement>
    | [
        Element<typeof AlgebraicWorkingStatement>,
        Element<typeof AlgebraicWorkingExplanation>,
      ],
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, AlgebraicWorkingRow);
  if (failedValidation) return null;
  const childrenArray = React.Children.toArray(props.children);
  const statement = React.cloneElement(childrenArray[0], {
    _index: props._index,
  });
  const explanation = childrenArray[1];
  return (
    <tr>
      {props._useRowNumbering && (
        <td style={TD_STYLE}>
          <StepIndicatorInternal step={props._index + 1} />
        </td>
      )}
      {statement}
      {explanation}
    </tr>
  );
}
AlgebraicWorkingRow.spec = {
  children: t.unionOfTypes(
    t.elementType(AlgebraicWorkingStatement),
    t.tuple([
      t.elementType(AlgebraicWorkingStatement),
      t.elementType(AlgebraicWorkingExplanation),
    ]),
  ),
};

type LatexString = string;
export function AlgebraicWorkingStatement(props: {|
  _index: number,
  lhs?: LatexString,
  relation: '=' | '\\neq' | '<' | '>' | '\\leq' | '\\geq' | '\\approx' | '⬚',
  rhs: LatexString,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    AlgebraicWorkingStatement,
  );
  const { _index, lhs, relation, rhs } = props;

  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    if (_index === 0 && lhs === undefined) {
      addErrors([
        {
          message:
            'lhs prop must always be supplied for the first AlgebraicWorkingStatement',
          ...locations.AlgebraicWorkingStatement,
        },
      ]);
    }
  });

  // This logic vertically aligns the latex expressions across the 3 <td>s
  // NOTE: vertical-align cannot be used as the AlgebraicWorkingExplanation may
  // be a large paragraph that wraps many lines, but should have no influence on
  // what is considered the vertical center of the equation.
  const latexNodes = React.useRef([]);
  const [vOffsets, setVOffsets] = React.useState([0, 0, 0]);
  React.useLayoutEffect(() => {
    const nonNullLatexNodes = latexNodes.current.filter(Boolean);
    const heights = nonNullLatexNodes.map(
      (node) => node.getBoundingClientRect().height,
    );
    const max = Math.max(...heights);
    const offsets = heights.map((h) => {
      if (h === max) return 0;
      return max / 2 - h / 2;
    });
    setVOffsets(offsets);
  }, [lhs, relation, rhs]);

  if (failedValidation) return null;
  return (
    <>
      <td style={{ ...TD_STYLE, paddingLeft: 10, textAlign: 'right' }}>
        <span
          ref={(node) => (latexNodes.current[0] = node)}
          style={{
            display: 'inline-block',
            transform: `translateY(${vOffsets[0]}px)`,
          }}
        >
          {lhs && <Latex expression={`\\displaystyle ${lhs}`} />}
        </span>
      </td>
      <td style={TD_STYLE}>
        <span
          ref={(node) => (latexNodes.current[1] = node)}
          style={{
            display: 'inline-block',
            transform: `translateY(${vOffsets[1]}px)`,
          }}
        >
          <Latex expression={`\\displaystyle ${relation}`} />
        </span>
      </td>
      <td style={{ ...TD_STYLE, paddingRight: 10 }}>
        <span
          ref={(node) => (latexNodes.current[2] = node)}
          style={{
            display: 'inline-block',
            transform: `translateY(${vOffsets[2]}px)`,
          }}
        >
          <Latex expression={`\\displaystyle ${rhs}`} />
        </span>
      </td>
    </>
  );
}
AlgebraicWorkingStatement.spec = {
  // This is only optional for rows 2->n
  lhs: t.optional(nonEmptyLatexChecker),
  relation: t.unionOfValues(
    '=',
    '\\neq',
    '<',
    '>',
    '\\leq',
    '\\geq',
    '\\approx',
    // This is not a relation, but serves as a fill-in-the-blank
    // for the student to insert a relation to satisfy the working
    // statement.
    '⬚',
  ),
  rhs: nonEmptyLatexChecker,
};

export function AlgebraicWorkingExplanation(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    AlgebraicWorkingExplanation,
  );
  if (failedValidation) return null;
  return (
    <td style={{ ...TD_STYLE, whiteSpace: 'normal' }}>{props.children}</td>
  );
}
AlgebraicWorkingExplanation.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(AlgebraicWorkingStepIndicator),
    ),
  ),
};

// 🚨🚨🚨🚨 Because the indicator gets used inside <Text> it is critical that
// the indicator has very prominent styling that feels highly inapporiate to use
// for any purpose other than referencing AlgebraicWorking steps. If we used
// subtle styling (such as just giving the text an accent color) this would
// be abused by content authors to highight random numbers inside <Text>
function StepIndicatorInternal({ step }: {| step: number |}) {
  const indicatorSize = 24;
  return (
    <span
      style={{
        display: 'inline-block',
        width: indicatorSize,
        height: indicatorSize,
        fontSize: 12,
        fontWeight: 600,
        color: 'white',
        backgroundColor: illustrationColor.governorBay,
        borderRadius: '50%',
        lineHeight: `${indicatorSize}px`,
        textAlign: 'center',
      }}
    >
      {step}
    </span>
  );
}

export function AlgebraicWorkingStepIndicator(props: {|
  step: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    AlgebraicWorkingStepIndicator,
  );
  // We don't have a great way to ensure this step actually refers
  // to one in the document, but we'll try to at least prevent people
  // trying to use this component for non-step number purposes.
  React.useEffect(() => {
    if (!(props.step > 0 && props.step < 50)) {
      addErrors([
        { message: 'step should be in range [0,50]', ...locations.step },
      ]);
    }
  });

  if (failedValidation) return null;
  return <StepIndicatorInternal step={props.step} />;
}
AlgebraicWorkingStepIndicator.spec = {
  step: t.number,
};
